import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { auth } from '../../firebase';
import Diff from './Diff';
import PullNav from './PullNav';
import {
    supportsFolderAccess,
    pickWriterFolder,
    scanWriterFolder,
    readHandleText,
    remoteToMarkdown,
    canonicalMarkdown,
    markdownToHtml,
    contentHash,
    fetchDocument,
    updateDocumentContent,
    createDocumentFromMarkdown,
    stampManifest,
} from './writerSyncUtils';
import '../../css/WriterPull.css';

const STATUS_ORDER = ['conflict', 'changed', 'new', 'error'];
const STATUS_TITLES = {
    conflict: 'Also changed here since Writer last synced — review carefully',
    changed: 'Updated in Bluebird Writer',
    new: 'New notes from Writer — will be created as Pages',
    error: 'Could not compare',
};
const OUTCOME_LABELS = {
    applied: 'Applied',
    created: 'Created',
    skipped: 'Skipped',
    error: 'Failed',
};

function WriterPull() {
    const [uid, setUid] = useState('');
    const [authResolved, setAuthResolved] = useState(false);
    const navigate = useNavigate();

    const [stage, setStage] = useState('intro'); // intro | planning | review
    const [progress, setProgress] = useState('');
    const [plan, setPlan] = useState(null); // rows
    const [skippedWorkspace, setSkippedWorkspace] = useState(0);
    const [upToDate, setUpToDate] = useState(0);
    const [missing, setMissing] = useState(0);
    const [approved, setApproved] = useState(() => new Set());
    const [expanded, setExpanded] = useState(() => new Set());
    const [outcomes, setOutcomes] = useState(null); // rowKey → outcome
    const [applying, setApplying] = useState(false);
    const [manifestStamped, setManifestStamped] = useState(null);
    const [error, setError] = useState('');
    const [scanState, setScanState] = useState(null); // { manifest, manifestHandle }

    useEffect(() => {
        const unsubscribe = auth.onAuthStateChanged((user) => {
            setUid(user ? user.uid : '');
            setAuthResolved(true);
        });
        return () => unsubscribe();
    }, []);

    const choose = async () => {
        setError('');
        let dirHandle;
        try {
            dirHandle = await pickWriterFolder();
        } catch {
            return; // user cancelled the picker
        }
        setStage('planning');
        try {
            setProgress('Reading your Writer folder…');
            const { files, manifest, manifestHandle } = await scanWriterFolder(dirHandle);
            setScanState({ manifest, manifestHandle });

            const rows = [];
            let wsSkipped = 0;
            let missingCount = 0;

            const manifestEntries = Object.entries(manifest?.docs || {});
            const mappedRels = new Set();

            // 1. Manifest-mapped personal documents
            for (const [key, entry] of manifestEntries) {
                if (key.startsWith('ws:')) {
                    wsSkipped++;
                    mappedRels.add(entry.rel);
                    continue;
                }
                mappedRels.add(entry.rel);
                const [type, ...nameParts] = key.split(':');
                const name = nameParts.join(':');
                setProgress(`Comparing ${name}…`);
                const file = files.find((f) => f.rel === entry.rel);
                if (!file) {
                    missingCount++;
                    continue; // no local file — nothing to pull from
                }
                try {
                    const localMd = await readHandleText(file.handle);
                    const remote = await fetchDocument(type, name, uid);
                    if (!remote) {
                        rows.push({ rowKey: key, type, name, rel: entry.rel, status: 'error', message: 'Not found in your account (deleted or renamed here?)' });
                        continue;
                    }
                    const remoteMd = remoteToMarkdown(remote.content || '');
                    const canonLocal = canonicalMarkdown(localMd);
                    if (canonLocal === remoteMd) {
                        setUpToDate((n) => n + 1);
                        continue;
                    }
                    rows.push({
                        rowKey: key,
                        type,
                        name,
                        rel: entry.rel,
                        status: remote.updated_at === entry.remoteUpdatedAt ? 'changed' : 'conflict',
                        localMd,
                        remote,
                        remoteMd,
                        canonLocal,
                    });
                } catch (err) {
                    rows.push({ rowKey: key, type, name, rel: entry.rel, status: 'error', message: err.message });
                }
            }

            // 2. Local notes that aren't mapped to any document yet
            for (const file of files) {
                if (mappedRels.has(file.rel)) continue;
                if (file.rel.startsWith('Bluebird/Workspaces/')) { wsSkipped++; continue; }
                const title = file.rel.split('/').pop().replace(/\.(md|markdown)$/i, '');
                setProgress(`Checking "${title}"…`);
                try {
                    const localMd = await readHandleText(file.handle);
                    const existing = await fetchDocument('page', title, uid);
                    if (existing) {
                        const remoteMd = remoteToMarkdown(existing.content || '');
                        const canonLocal = canonicalMarkdown(localMd);
                        if (canonLocal === remoteMd) {
                            setUpToDate((n) => n + 1);
                            continue;
                        }
                        rows.push({
                            rowKey: `Page:${title}`,
                            type: 'Page',
                            name: title,
                            rel: file.rel,
                            status: 'conflict',
                            message: 'A Page with this name already exists — approving overwrites it',
                            localMd,
                            remote: existing,
                            remoteMd,
                            canonLocal,
                        });
                    } else {
                        rows.push({
                            rowKey: `new:${file.rel}`,
                            type: 'Page',
                            name: title,
                            rel: file.rel,
                            status: 'new',
                            localMd,
                        });
                    }
                } catch (err) {
                    rows.push({ rowKey: `new:${file.rel}`, type: 'Page', name: title, rel: file.rel, status: 'error', message: err.message });
                }
            }

            setPlan(rows);
            setSkippedWorkspace(wsSkipped);
            setMissing(missingCount);
            setApproved(new Set());
            setExpanded(new Set());
            setOutcomes(null);
            setManifestStamped(null);
            setStage('review');
        } catch (err) {
            setError(err.message || String(err));
            setStage('intro');
        } finally {
            setProgress('');
        }
    };

    const toggleApproval = (rowKey) => {
        if (outcomes) return;
        setApproved((prev) => {
            const next = new Set(prev);
            if (next.has(rowKey)) next.delete(rowKey);
            else next.add(rowKey);
            return next;
        });
    };

    const toggleExpanded = (rowKey) => {
        setExpanded((prev) => {
            const next = new Set(prev);
            if (next.has(rowKey)) next.delete(rowKey);
            else next.add(rowKey);
            return next;
        });
    };

    const apply = async () => {
        if (!plan || !approved.size) return;
        setApplying(true);
        const results = new Map();
        const stamps = [];
        let done = 0;
        for (const row of plan) {
            if (!approved.has(row.rowKey) || row.status === 'error') {
                results.set(row.rowKey, { outcome: 'skipped' });
                continue;
            }
            setProgress(`Applying ${++done}/${approved.size}: ${row.name}…`);
            try {
                const html = markdownToHtml(row.localMd);
                if (row.status === 'new') {
                    await createDocumentFromMarkdown(row.name, uid, html);
                } else {
                    await updateDocumentContent(row.type, row.name, uid, row.remote, html);
                }
                // Re-fetch for the fresh updated_at so Writer's manifest agrees.
                const fresh = await fetchDocument(row.type, row.name, uid);
                if (fresh) {
                    stamps.push({
                        key: `${row.type[0].toUpperCase()}${row.type.slice(1).toLowerCase()}:${row.name}`,
                        rel: row.rel,
                        hash: contentHash(row.localMd),
                        remoteUpdatedAt: fresh.updated_at,
                    });
                }
                results.set(row.rowKey, { outcome: row.status === 'new' ? 'created' : 'applied' });
            } catch (err) {
                results.set(row.rowKey, { outcome: 'error', message: err.message });
            }
        }
        if (stamps.length && scanState?.manifest) {
            const ok = await stampManifest(scanState.manifestHandle, scanState.manifest, stamps);
            setManifestStamped(ok);
        }
        setOutcomes(results);
        setApplying(false);
        setProgress('');
    };

    const pending = plan || [];
    const summary = () => {
        if (!outcomes) return null;
        const counts = {};
        for (const r of outcomes.values()) counts[r.outcome] = (counts[r.outcome] || 0) + 1;
        const parts = [];
        if (counts.applied) parts.push(`${counts.applied} updated`);
        if (counts.created) parts.push(`${counts.created} created`);
        if (counts.skipped) parts.push(`${counts.skipped} left untouched`);
        if (counts.error) parts.push(`${counts.error} failed`);
        return parts.join(' · ');
    };

    if (!authResolved) {
        return (
            <div className="WriterPull__page">
                <p className="WriterPull__muted">Checking authentication…</p>
            </div>
        );
    }

    const navItems = [
        { label: 'Home', icon: 'fas fa-home', to: '/' },
        { label: 'Your Documents', icon: 'fas fa-file-lines', to: '/documentation' },
    ];

    if (!uid) {
        return (
            <div className="WriterPull__page">
                <div className="WriterPull__card">
                    <PullNav items={[{ label: 'Home', icon: 'fas fa-home', to: '/' }]} />
                    <h2>Pull from Bluebird Writer</h2>
                    <p>You need to be signed in to pull changes from your Writer folder.</p>
                    <button className="WriterPull__primary" onClick={() => navigate('/login')}>Login</button>
                </div>
            </div>
        );
    }

    return (
        <div className="WriterPull__page">
            <div className="WriterPull__card">
                <PullNav items={navItems} disabled={stage === 'planning' || applying} />
                <h2>Pull from Bluebird Writer</h2>

                {stage === 'intro' && (
                    <>
                        <p>
                            Choose the folder Bluebird Writer saves your notes in. We'll compare each note
                            against your documents here and show you every difference. <strong>Nothing is
                            changed unless you approve it, document by document.</strong>
                        </p>
                        <ul className="WriterPull__notes">
                            <li>Notes Writer pulled earlier are matched via its sync manifest.</li>
                            <li>Local notes that aren't documents yet can be created as new Pages.</li>
                            <li>
                                Team-workspace notes are skipped here — each workspace has its own
                                "Pull from Writer" in its Workspace Settings.
                            </li>
                            <li>Every update keeps a history snapshot, so changes can be recovered.</li>
                        </ul>
                        {!supportsFolderAccess() && (
                            <p className="WriterPull__error">
                                This browser can't open local folders (File System Access API). Chrome and
                                Edge support it.
                            </p>
                        )}
                        {error && <p className="WriterPull__error">{error}</p>}
                        <button
                            className="WriterPull__primary"
                            disabled={!supportsFolderAccess()}
                            onClick={choose}
                        >
                            Choose Writer folder…
                        </button>
                    </>
                )}

                {stage === 'planning' && <p className="WriterPull__muted">{progress || 'Comparing…'}</p>}

                {stage === 'review' && (
                    <>
                        <p className="WriterPull__summary-line">
                            {pending.length
                                ? `${pending.length} ${pending.length === 1 ? 'difference' : 'differences'}`
                                : 'Everything is in sync'}
                            {` · ${upToDate} up to date`}
                            {missing > 0 && ` · ${missing} not present locally`}
                            {skippedWorkspace > 0 && ` · ${skippedWorkspace} workspace notes skipped`}
                        </p>

                        {STATUS_ORDER.map((status) => {
                            const group = pending.filter((p) => p.status === status);
                            if (!group.length) return null;
                            return (
                                <div key={status} className="WriterPull__group">
                                    <h3 className={`WriterPull__group-title WriterPull__group-title--${status}`}>
                                        {STATUS_TITLES[status]} ({group.length})
                                    </h3>
                                    {group.map((row) => {
                                        const outcome = outcomes?.get(row.rowKey);
                                        return (
                                            <div key={row.rowKey} className="WriterPull__row">
                                                <div className="WriterPull__row-main">
                                                    <label className="WriterPull__row-label" title={row.message || row.rel}>
                                                        <input
                                                            type="checkbox"
                                                            disabled={row.status === 'error' || !!outcomes}
                                                            checked={approved.has(row.rowKey)}
                                                            onChange={() => toggleApproval(row.rowKey)}
                                                        />
                                                        <span className="WriterPull__type">{row.type}</span>
                                                        <span className="WriterPull__name">{row.name}</span>
                                                    </label>
                                                    {row.status !== 'error' && (
                                                        <button
                                                            className="WriterPull__ghost"
                                                            onClick={() => toggleExpanded(row.rowKey)}
                                                        >
                                                            {expanded.has(row.rowKey) ? 'Hide' : row.status === 'new' ? 'Preview' : 'Diff'}
                                                        </button>
                                                    )}
                                                    {outcome && (
                                                        <span
                                                            className={`WriterPull__outcome WriterPull__outcome--${outcome.outcome}`}
                                                            title={outcome.message || ''}
                                                        >
                                                            {OUTCOME_LABELS[outcome.outcome]}
                                                        </span>
                                                    )}
                                                </div>
                                                {row.message && !outcomes && (
                                                    <p className="WriterPull__row-note">{row.message}</p>
                                                )}
                                                {expanded.has(row.rowKey) && (
                                                    <Diff oldText={row.remoteMd || ''} newText={row.status === 'new' ? row.localMd : row.canonLocal} />
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            );
                        })}

                        <div className="WriterPull__footer">
                            <span className="WriterPull__muted">
                                {applying
                                    ? progress
                                    : outcomes
                                        ? `${summary()}${manifestStamped === false ? ' · could not update the Writer manifest (folder was read-only)' : ''}`
                                        : pending.length
                                            ? `${approved.size} of ${pending.length} approved — unapproved documents stay exactly as they are`
                                            : ''}
                            </span>
                            <span className="WriterPull__footer-actions">
                                <button className="WriterPull__ghost" disabled={applying} onClick={() => { setStage('intro'); setPlan(null); setOutcomes(null); setUpToDate(0); }}>
                                    {outcomes ? 'Start over' : 'Back'}
                                </button>
                                {!outcomes && pending.length > 0 && (
                                    <button
                                        className="WriterPull__primary"
                                        disabled={applying || approved.size === 0}
                                        onClick={apply}
                                    >
                                        Apply {approved.size || ''} approved
                                    </button>
                                )}
                            </span>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
}

export default WriterPull;
